import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { CommonModule, registerLocaleData } from '@angular/common'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { MatDialogModule } from '@angular/material/dialog'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'

import { NgModule, LOCALE_ID, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core'
import { FileUploadModule } from '@iplab/ngx-file-upload'
import { NgbModule, NgbDatepickerI18n } from '@ng-bootstrap/ng-bootstrap'

import { NgxSpinnerModule } from 'ngx-spinner'
import { TranslateLoader, TranslateModule } from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'

import { AppRoutingModule } from './app-routing.module'

import { AppComponent } from './app.component'
import { HeaderComponent } from './components/header/header.component'
import { FooterComponent } from './components/footer/footer.component'
import { HomeComponent } from './components/home/home.component'
import { AboutUsComponent } from './components/about-us/about-us.component'
import { FaqComponent } from './components/faq/faq.component'
import { CareersComponent } from './components/careers/careers.component'
import { CustomerIntentComponent } from './components/customer-intent/customer-intent.component'
import { ContactComponent } from './components/contact/contact.component'
import { PrivacyPolicyComponent } from './components/privacy-policy/privacy-policy.component'
import { AodaComponent } from './components/aoda/aoda.component'
import { AccessiblePolicyComponent } from './components/accessible-policy/accessible-policy.component'
import { RequestAccountValidationComponent } from './components/customer-requests/request-account-validation/request-account-validation.component'
import { RequestAddressChangeDetailComponent } from './components/customer-requests/request-address-change/detail/request-address-change-detail.component'
import { RequestBankAccountChangeDetailComponent } from './components/customer-requests/request-bank-account-change/detail/request-bank-account-change-detail.component'
import { RequestEmailChangeDetailComponent } from './components/customer-requests/request-email-change/detail/request-email-change-detail.component'
import { RequestEmailChangeValidationComponent } from './components/customer-requests/request-email-change/validation/request-email-change-validation.component'
import { RequestLeaseTransferDetailComponent } from './components/customer-requests/request-lease-transfer/detail/request-lease-transfer-detail.component'
import { RequestLeaseTransferSummaryComponent } from './components/customer-requests/request-lease-transfer/summary/request-lease-transfer-summary.component'
import { RequestListComponent } from './components/customer-requests/request-list/request-list.component'
import { RequestNameChangeDetailComponent } from './components/customer-requests/request-name-change/detail/request-name-change-detail.component'
import { RequestOtherChangeDetailComponent } from './components/customer-requests/request-other-change/detail/request-other-change-detail.component'
import { RequestPaymentDateChangeDetailComponent } from './components/customer-requests/request-payment-date-change/detail/request-payment-date-change-detail.component'
import { RequestPhoneChangeDetailComponent } from './components/customer-requests/request-phone-change/detail/request-phone-change-detail.component'
import { RequestStatusDetailComponent } from './components/customer-requests/request-status/detail/request-status-detail.component'
import { RequestSummaryComponent } from './components/customer-requests/request-summary/request-summary.component'

import { ErrorMessageModalComponent } from './components/modals/error-message-modal/error-message-modal.component'
import { PopupMessageModalComponent } from './components/modals/popup-message-modal/popup-message-modal.component'
import { RequestConfirmationModalComponent } from './components/modals/request-confirmation-modal/request-confirmation-modal.component'
import { RequestProceedModalComponent } from './components/modals/request-proceed-modal/request-proceed-modal.component'
import { RequestSummaryBankAccountChangeModalComponent } from './components/modals/request-summary-bank-account-change-modal/request-summary-bank-account-change-modal.component'
import { RequestSummaryLeaseTransferModalComponent } from './components/modals/request-summary-lease-transfer-modal/request-summary-lease-transfer-modal.component'
import { RequestSummaryModalComponent } from './components/modals/request-summary-modal/request-summary-modal.component'
import { SessionTimeoutModalComponent } from './components/modals/session-timeout-modal/session-timeout-modal.component'
import { SubmitInsuranceModalComponent } from './components/modals/submit-insurance-modal/submit-insurance-modal.component'

import { environment } from '../environments/environment'
import { AuthenticationService } from './services/authentication.service'
import { CustomerRequestsService } from './services/customer-requests.service'
import { SiteService } from './services/site.service'
import { DatepickerService } from './services/datepicker.service'
import { AnalyticsService } from './services/analytics.service'

import localeFr from '@angular/common/locales/fr'
import localeEn from '@angular/common/locales/en'

registerLocaleData(localeFr)
registerLocaleData(localeEn)

@NgModule({ declarations: [
        AppComponent,
        HeaderComponent,
        FooterComponent,
        HomeComponent,
        AboutUsComponent,
        FaqComponent,
        CareersComponent,
        CustomerIntentComponent,
        ContactComponent,
        PrivacyPolicyComponent,
        AodaComponent,
        AccessiblePolicyComponent,
        RequestAccountValidationComponent,
        RequestAddressChangeDetailComponent,
        RequestBankAccountChangeDetailComponent,
        RequestEmailChangeDetailComponent,
        RequestEmailChangeValidationComponent,
        RequestLeaseTransferDetailComponent,
        RequestLeaseTransferSummaryComponent,
        RequestListComponent,
        RequestNameChangeDetailComponent,
        RequestOtherChangeDetailComponent,
        RequestPaymentDateChangeDetailComponent,
        RequestPhoneChangeDetailComponent,
        RequestStatusDetailComponent,
        RequestSummaryComponent,
        RequestConfirmationModalComponent,
        RequestProceedModalComponent,
        RequestSummaryBankAccountChangeModalComponent,
        RequestSummaryLeaseTransferModalComponent,
        RequestSummaryModalComponent,
        ErrorMessageModalComponent,
        PopupMessageModalComponent,
        SessionTimeoutModalComponent,
        SubmitInsuranceModalComponent
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [AppRoutingModule,
        BrowserModule,
        BrowserAnimationsModule,
        CommonModule,
        FileUploadModule,
        NgbModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatInputModule,
        MatDialogModule,
        NgxSpinnerModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: httpTranslateLoader,
                deps: [HttpClient]
            },
            defaultLanguage: 'en',
        })], 
        providers: [
            DatepickerService,
            { provide: 'BASE_API_URL', useValue: environment.apiUrl },
            { provide: NgbDatepickerI18n, useExisting: DatepickerService },
            { provide: LOCALE_ID, useValue: 'en' },
            SiteService,
            AuthenticationService,
            CustomerRequestsService,
            AnalyticsService,
            provideHttpClient(withInterceptorsFromDi())
        ] })
export class AppModule { }

export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http)
}
